import { useState } from 'react';
import './Legal.css';

const COMPANY      = 'GoodGPT';
const LEGAL_ENTITY = 'GOSHOPIN NOW (OPC) PRIVATE LIMITED';
const CIN          = 'U47912HR2024OPC120697';
const EMAIL        = 'info@goodgpt.site';
const WEBSITE      = 'goodgpt.site';
const ADDRESS      = 'C-042, 4th Floor, Supermart, DLF Phase IV, Galleria DLF-IV, Gurgaon, Haryana, 122009, India';
const ADDRESS_CITY = 'Gurgaon, Haryana, India';
const INCORPORATED = '12 April 2024';
const GSTIN        = '06AALCG1828J1ZG';

function TermsContent() {
  return (
    <div className="legal-body">
      <p className="legal-updated">Last updated: October 1, 2026</p>

      <h2>1. Acceptance of Terms</h2>
      <p>By accessing or using {COMPANY} ("the Service"), you agree to be bound by these Terms and Conditions. If you do not agree, please do not use the Service.</p>

      <h2>2. Description of Service</h2>
      <p>{COMPANY} is an AI-powered platform operated by {LEGAL_ENTITY} (CIN: {CIN}, GSTIN: {GSTIN}) offering grammar correction, content creation, language learning, quiz generation, simulator scenarios, document chat, course access, and consultation booking services. The Service is accessible via the website ({WEBSITE}), web app, and the {COMPANY} Chrome Extension.</p>

      <h2>3. User Accounts</h2>
      <p>You must provide accurate information when creating an account. You are responsible for maintaining the confidentiality of your credentials. You must be at least 13 years old to use this Service.</p>

      <h2>4. Subscriptions and Payments</h2>
      <p>Certain features require a paid subscription. Subscription fees are billed in advance and are non-refundable except as required by law. We use PayPal to process payments — by subscribing you also agree to PayPal's terms of service. Prices may change with 30 days' notice.</p>
      <p>All fees are exclusive of Goods and Services Tax (GST). GST at the prevailing rate is charged on subscriptions where applicable, and {LEGAL_ENTITY} is registered under GSTIN {GSTIN}. Taxes collected by us are deposited with the Government of India and a GST invoice is issued on request. Where tax must be withheld at source, the applicable deduction is made and reflected on your invoice.</p>

      <h2>5. Consultation Bookings</h2>
      <p>Consultation sessions are booked for 1-hour slots. Cancellations must be made at least 24 hours before the session for a refund. No-shows are non-refundable. Consultants are independent professionals; {COMPANY} facilitates the booking but is not liable for the quality of consultations.</p>

      <h2>6. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service for any unlawful purpose</li>
        <li>Generate content that is harmful, abusive, defamatory, or infringes third-party rights</li>
        <li>Attempt to reverse-engineer, scrape, or circumvent any part of the Service</li>
        <li>Share your account credentials with others</li>
        <li>Use the Service to train competing AI models</li>
      </ul>

      <h2>7. AI-Generated Content</h2>
      <p>Responses generated by AI are for informational purposes only and may not be accurate. {COMPANY} does not guarantee the correctness of AI output. Do not rely on AI responses for medical, legal, financial, or safety-critical decisions.</p>

      <h2>8. Intellectual Property and Data Licence</h2>
      <p>The {COMPANY} platform, including its design, code, and branding, is owned by {LEGAL_ENTITY} (CIN: {CIN}). You retain ownership of the original content you create outside the Service.</p>
      <p>By using the Service, you grant {LEGAL_ENTITY} a worldwide, royalty-free, sublicensable, perpetual licence to use, store, reproduce, modify, adapt, publish, translate, distribute, and create derivative works from any content you submit to or generate through the Service. This licence is used to operate, deliver, maintain, and improve the Service and our AI systems, including training and fine-tuning AI models on an anonymised basis. This licence survives termination of your account.</p>

      <h2>9. Chrome Extension</h2>
      <p>The {COMPANY} Chrome Extension reads visible text from webpages you are actively viewing solely to perform the AI task you request (e.g. summarise, correct, paraphrase). This text is transmitted to our servers over HTTPS. The Extension also monitors tab URL changes exclusively to detect PayPal payment return URLs — no other browsing history is recorded or transmitted.</p>

      <h2>10. Courses and Learning Content</h2>
      <p>Course content is provided for personal educational use only. Redistribution, resale, or sharing of course materials is prohibited. Access to paid courses is non-transferable.</p>

      <h2>11. Disclaimers</h2>
      <p>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF HARMFUL COMPONENTS.</p>

      <h2>12. Limitation of Liability</h2>
      <p>TO THE FULLEST EXTENT PERMITTED BY LAW, {LEGAL_ENTITY} AND ITS AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR USE OF THE SERVICE.</p>

      <h2>13. Governing Law</h2>
      <p>These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Gurugram, Haryana, India.</p>

      <h2>14. Changes to Terms</h2>
      <p>We may update these Terms at any time. Continued use of the Service after changes constitutes acceptance. We will notify you of material changes via email or in-app notice.</p>

      <h2>15. Contact</h2>
      <p>For questions about these Terms, contact us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a> or visit <strong>{WEBSITE}</strong>.</p>
      <p>
        <strong>{LEGAL_ENTITY}</strong><br />
        CIN: {CIN}<br />
        GSTIN: {GSTIN}<br />
        Incorporated: {INCORPORATED}<br />
        {ADDRESS}<br />
        Email: <a href={`mailto:${EMAIL}`}>{EMAIL}</a><br />
        Website: <a href={`https://${WEBSITE}`} target="_blank" rel="noopener noreferrer">{WEBSITE}</a>
      </p>
    </div>
  );
}

function PrivacyContent() {
  return (
    <div className="legal-body">
      <p className="legal-updated">Last updated: October 1, 2026</p>

      <h2>1. Introduction</h2>
      <p>{COMPANY} ("we", "us", "our") is a service operated by {LEGAL_ENTITY} (CIN: {CIN}, GSTIN: {GSTIN}), incorporated on {INCORPORATED} and registered in {ADDRESS_CITY}. This Privacy Policy explains how we collect, use, store, and share your information when you use our website, web app, or Chrome Extension.</p>

      <h2>2. Information We Collect</h2>
      <h3>Information you provide directly</h3>
      <ul>
        <li><strong>Account:</strong> Email address, name, mobile number</li>
        <li><strong>Profile:</strong> Name, mobile number (optional)</li>
        <li><strong>Consultation booking:</strong> Name, email, phone, session notes</li>
        <li><strong>Documents:</strong> Files you upload for AI analysis</li>
        <li><strong>Payments:</strong> Handled entirely by PayPal — we do not store card details</li>
      </ul>
      <h3>Information collected automatically</h3>
      <ul>
        <li>Chat messages, prompts, and AI responses</li>
        <li>Quiz attempts, scores, and session timings</li>
        <li>Simulator and coaching session history</li>
        <li>Usage patterns and feature interactions</li>
        <li><strong>Chrome Extension — page text:</strong> When you use the "Summarize This Page", "Correct", "Paraphrase", "Write Blog", or "Ask {COMPANY}" features, up to 4,000 characters of visible text from the active webpage are extracted and transmitted to our servers for AI processing.</li>
      </ul>

      <h2>3. How We Use Your Information</h2>
      <ul>
        <li>To provide, operate, and improve the Service</li>
        <li>To authenticate you and secure your account (OTP via email)</li>
        <li>To process subscription payments through PayPal</li>
        <li>To generate GST invoices as required by Indian tax law (GSTIN {GSTIN})</li>
        <li>To send transactional emails (OTP, booking confirmations)</li>
        <li>To track learning progress and generate personalised recommendations</li>
        <li>To train, fine-tune, and improve our AI models using anonymised and aggregated interactions</li>
        <li>To conduct internal research, product analytics, and development</li>
        <li>To respond to support requests</li>
      </ul>

      <h2>4. AI Processing and Model Training</h2>
      <p>Your messages, uploaded documents, and AI interactions may be sent to third-party AI providers (OpenAI, Anthropic, etc.) to generate responses. These providers process data under their own privacy policies.</p>
      <p>We also use anonymised and aggregated versions of user interactions to train, evaluate, and improve our own AI systems. Individual identifying information is removed before any such use. You may opt out of having your interactions used for AI training by emailing us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a>; note that opting out does not affect data already processed.</p>
      <p>When you use the {COMPANY} Chrome Extension to perform any AI task, the selected or page text is sent securely to our backend server at api.goodgpt.site for processing.</p>

      <h2>5. Data Ownership and Licence</h2>
      <p>By using the Service, you grant {LEGAL_ENTITY} a worldwide, royalty-free, sublicensable, perpetual licence to use, store, reproduce, modify, adapt, publish, translate, distribute, and create derivative works from any content you submit to or generate through the Service. This licence is used to operate, improve, and develop the Service and its underlying AI systems. This licence survives account deletion.</p>
      <p>Anonymised and aggregated data derived from your use of the Service may be retained and used by us indefinitely for research, analytics, and AI development, even after your account is deleted.</p>

      <h2>6. Data Storage and Security</h2>
      <p>Your data is stored on AWS infrastructure (RDS PostgreSQL) located in the US East region. We implement industry-standard security measures including encrypted connections (HTTPS/TLS) and hashed authentication tokens. No system is 100% secure — use strong, unique passwords.</p>

      <h2>7. Data Retention</h2>
      <p>We retain your personal account data for as long as your account is active. If you delete your account, identifiable personal data is removed within 90 days, except where retention is required for legal, tax, or compliance obligations (up to 7 years as required under Indian law). Chat history, uploaded documents, and AI interaction logs may be retained in anonymised or aggregated form indefinitely for AI training and product improvement purposes.</p>

      <h2>8. Sharing of Information</h2>
      <p>We do not sell your personally identifiable information to third parties. We share data only with:</p>
      <ul>
        <li><strong>AI providers</strong> (OpenAI, Anthropic) — to generate responses to your requests</li>
        <li><strong>PayPal</strong> — to process payments</li>
        <li><strong>Google / Firebase</strong> — the Chrome Extension fetches the backend server URL from Firebase Realtime Database on startup; no personal data is transmitted in this request</li>
        <li><strong>Consultants</strong> — name, email, phone, and notes you provide for a booked session</li>
        <li><strong>Business transfers</strong> — in the event of a merger, acquisition, or sale of assets, your data may be transferred to the acquiring entity</li>
        <li><strong>Legal authorities</strong> — if required by applicable law or court order</li>
      </ul>

      <h2>9. Cookies and Local Storage</h2>
      <p>We use browser localStorage and chrome.storage.local (in the Chrome Extension) to store your session token, authentication state, and conversation history on your device. We do not use third-party advertising cookies. Clearing your browser or extension storage will log you out.</p>

      <h2>10. Chrome Extension — Additional Disclosures</h2>
      <p>The {COMPANY} Chrome Extension requires the following permissions and uses them as described:</p>
      <ul>
        <li><strong>activeTab / content scripts:</strong> Reads visible text from the webpage you are currently viewing, only when you trigger an AI action. No text is read passively or in background tabs.</li>
        <li><strong>tabs:</strong> Monitors tab URL changes exclusively to detect when PayPal redirects back to {WEBSITE} after a payment — no other URLs are inspected or recorded.</li>
        <li><strong>storage:</strong> Stores your authentication token, chat history, and daily usage data locally in chrome.storage.local.</li>
        <li><strong>contextMenus:</strong> Adds right-click menu options for selected text on any page.</li>
      </ul>

      <h2>11. Your Rights</h2>
      <p>Under applicable law (including India's Digital Personal Data Protection Act, 2023), you have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you</li>
        <li>Correct inaccurate data (via the Profile settings)</li>
        <li>Request deletion of your account and identifiable personal data</li>
        <li>Opt out of AI training use of your interactions</li>
        <li>Object to or restrict certain processing</li>
        <li>Data portability (request an export of your data)</li>
      </ul>
      <p>Note that exercising deletion rights does not affect anonymised or aggregated data already incorporated into our AI systems. To exercise any of these rights, email us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.</p>

      <h2>12. Children's Privacy</h2>
      <p>The Service is not directed at children under 13. We do not knowingly collect personal information from children under 13. If we become aware of such collection, we will delete it promptly.</p>

      <h2>13. Changes to This Policy</h2>
      <p>We may update this Privacy Policy from time to time. We will notify you of significant changes via email or in-app notice. Continued use of the Service after the effective date constitutes acceptance of the updated policy.</p>

      <h2>14. Contact Us</h2>
      <p>For privacy-related questions, data requests, or to opt out of AI training use, contact our Data Controller:</p>
      <p>
        <strong>{LEGAL_ENTITY}</strong><br />
        CIN: {CIN}<br />
        GSTIN: {GSTIN}<br />
        Incorporated: {INCORPORATED}<br />
        {ADDRESS}<br />
        Email: <a href={`mailto:${EMAIL}`}>{EMAIL}</a><br />
        Website: <strong>{WEBSITE}</strong>
      </p>
    </div>
  );
}

export function LegalPage({ initialTab = 'terms' }) {
  const [tab, setTab] = useState(initialTab);
  return (
    <div className="legal-page">
      <div className="legal-page-inner">
        <div className="legal-header">
          <div className="legal-tabs">
            <button className={`legal-tab ${tab === 'terms' ? 'active' : ''}`} onClick={() => setTab('terms')}>
              Terms &amp; Conditions
            </button>
            <button className={`legal-tab ${tab === 'privacy' ? 'active' : ''}`} onClick={() => setTab('privacy')}>
              Privacy Policy
            </button>
          </div>
          <a href="/" className="legal-home-link">← Back to GoodGPT</a>
        </div>
        <div className="legal-content">
          {tab === 'terms' ? <TermsContent /> : <PrivacyContent />}
        </div>
      </div>
    </div>
  );
}

export default function Legal({ initialTab = 'terms', onClose }) {
  const [tab, setTab] = useState(initialTab);

  return (
    <div className="legal-overlay" onClick={onClose}>
      <div className="legal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="legal-header">
          <div className="legal-tabs">
            <button className={`legal-tab ${tab === 'terms' ? 'active' : ''}`} onClick={() => setTab('terms')}>
              Terms &amp; Conditions
            </button>
            <button className={`legal-tab ${tab === 'privacy' ? 'active' : ''}`} onClick={() => setTab('privacy')}>
              Privacy Policy
            </button>
          </div>
          <button className="legal-close" onClick={onClose}>×</button>
        </div>
        <div className="legal-content">
          {tab === 'terms'   ? <TermsContent />   : <PrivacyContent />}
        </div>
        <div className="legal-footer">
          <button className="legal-close-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
